// ---------------------------------------------------------------------------
// music.store.ts — the player's state
// ---------------------------------------------------------------------------
//
// zustand, matching the calling feature (modules/calls/hooks/useStaffCallClient).
// The store holds WHAT should be happening; `MusicPlayerHost` owns the single
// <audio> element and makes it so. Keeping the element out of here is what lets
// the player survive navigation: the store is module-level, and the host lives in
// providers.tsx, which is the only node React never remounts.
//
// Three decisions that are easy to get wrong:
//
//   positionSec is stored in WHOLE SECONDS. `timeupdate` fires about four times a
//   second, and putting a float here re-renders every subscriber at 4 Hz for a
//   display that shows seconds.
//
//   The queue holds IDS, not track objects, with a separate `tracks` map. A local
//   file's status changes (it goes missing, permission lapses) and duplicating
//   the object into the queue would leave two copies to keep in step.
//
//   Preferences persist through localStorage rather than IndexedDB. The codebase
//   hand-rolls IndexedDB for the theme, but that is a whole async round trip for
//   four scalars; volume is a per-device convenience, and losing it in a private
//   window is not a bug worth async code.
// ---------------------------------------------------------------------------

import { create } from 'zustand';

import {
  type QueueState,
  type RepeatMode,
  buildShuffleOrder,
  firstIndex,
  moveItem,
  nextPlayableIndex,
  previousIndex,
  removeAt,
} from '../lib/queue';
import { type LocalTrackStatus, type Track, isLocalTrack, isPlayable, isStockTrack } from '../lib/track';

export type PlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused';
export type PanelMode = 'closed' | 'compact' | 'expanded';

const PREFS_KEY = 'hms-music-prefs';

interface PersistedPrefs {
  volume: number;
  muted: boolean;
  repeat: RepeatMode;
  shuffle: boolean;
  scheduleEnabled: boolean;
}

const DEFAULT_PREFS: PersistedPrefs = {
  volume: 0.7,
  muted: false,
  repeat: 'all',
  shuffle: false,
  scheduleEnabled: true,
};

/**
 * Read preferences, tolerating every way storage can fail.
 *
 * A private window, blocked site data or a half-written value must not stop the
 * player loading — the cost of being wrong here is a default volume.
 */
function loadPrefs(): PersistedPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<PersistedPrefs>;
    return {
      volume:
        typeof parsed.volume === 'number' && parsed.volume >= 0 && parsed.volume <= 1
          ? parsed.volume
          : DEFAULT_PREFS.volume,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULT_PREFS.muted,
      repeat:
        parsed.repeat === 'off' || parsed.repeat === 'one' || parsed.repeat === 'all'
          ? parsed.repeat
          : DEFAULT_PREFS.repeat,
      shuffle: typeof parsed.shuffle === 'boolean' ? parsed.shuffle : DEFAULT_PREFS.shuffle,
      scheduleEnabled:
        typeof parsed.scheduleEnabled === 'boolean'
          ? parsed.scheduleEnabled
          : DEFAULT_PREFS.scheduleEnabled,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: PersistedPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full or blocked. A forgotten volume is not worth surfacing.
  }
}

export interface MusicState {
  /** Every track the player knows about, by id. */
  tracks: Record<string, Track>;
  /**
   * The stock library's ids in the order the server lists them. Play with an
   * empty queue starts here — so the big play button works the moment the
   * panel opens, instead of sitting disabled until a track is picked.
   */
  libraryOrder: string[];
  /** Ids in play order. */
  queue: string[];
  currentIndex: number;

  status: PlaybackStatus;
  /** Whole seconds. */
  positionSec: number;
  durationSec: number | null;

  volume: number;
  muted: boolean;
  repeat: RepeatMode;
  shuffle: boolean;
  shuffleOrder: number[];

  panel: PanelMode;

  /**
   * True once the user has clicked something in the player.
   *
   * Browsers reject `play()` before a gesture, so a schedule firing beforehand
   * must prompt rather than fail into silence.
   */
  userHasInteracted: boolean;

  scheduleEnabled: boolean;
  activeScheduleId: string | null;
  /** Set when a schedule wants to start but cannot without a gesture. */
  scheduleAwaitingGesture: string | null;

  lastError: string | null;

  /** Seconds the host should seek to, then clear. Null when there is nothing pending. */
  pendingSeekSec: number | null;

  // --- library -------------------------------------------------------------
  upsertTracks: (tracks: readonly Track[]) => void;
  removeTrack: (id: string) => void;
  setLocalStatus: (id: string, status: LocalTrackStatus) => void;

  // --- queue ---------------------------------------------------------------
  playNow: (ids: readonly string[], startAtId?: string) => void;
  enqueue: (ids: readonly string[]) => void;
  removeFromQueue: (index: number) => void;
  moveInQueue: (from: number, to: number) => void;
  clearQueue: () => void;

  // --- transport -----------------------------------------------------------
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  next: (auto?: boolean) => void;
  previous: () => void;
  jumpTo: (index: number) => void;
  seek: (sec: number) => void;
  consumeSeek: () => number | null;

  // --- settings ------------------------------------------------------------
  setVolume: (v: number) => void;
  toggleMute: () => void;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  setScheduleEnabled: (on: boolean) => void;

  // --- panel ---------------------------------------------------------------
  setPanel: (mode: PanelMode) => void;
  toggleCompact: () => void;
  collapsePanel: () => void;

  // --- reports from the <audio> element ------------------------------------
  reportStatus: (status: PlaybackStatus) => void;
  reportPosition: (sec: number) => void;
  reportDuration: (sec: number | null) => void;
  reportError: (message: string | null) => void;
  markInteracted: () => void;

  // --- schedule ------------------------------------------------------------
  setActiveSchedule: (id: string | null) => void;
  setScheduleAwaitingGesture: (id: string | null) => void;
}

const prefs = loadPrefs();

export const useMusicStore = create<MusicState>((set, get) => {
  /** The queue as the pure helpers in ../lib/queue expect it. */
  const queueState = (): QueueState => {
    const s = get();
    return {
      ids: s.queue,
      currentIndex: s.currentIndex,
      shuffle: s.shuffle,
      shuffleOrder: s.shuffleOrder,
      repeat: s.repeat,
    };
  };

  const playable = (id: string): boolean => {
    const t = get().tracks[id];
    return t ? isPlayable(t) : false;
  };

  const persist = () => {
    const s = get();
    savePrefs({
      volume: s.volume,
      muted: s.muted,
      repeat: s.repeat,
      shuffle: s.shuffle,
      scheduleEnabled: s.scheduleEnabled,
    });
  };

  return {
    tracks: {},
    libraryOrder: [],
    queue: [],
    currentIndex: -1,

    status: 'idle',
    positionSec: 0,
    durationSec: null,

    volume: prefs.volume,
    muted: prefs.muted,
    repeat: prefs.repeat,
    shuffle: prefs.shuffle,
    shuffleOrder: [],

    panel: 'closed',
    userHasInteracted: false,

    scheduleEnabled: prefs.scheduleEnabled,
    activeScheduleId: null,
    scheduleAwaitingGesture: null,

    lastError: null,
    pendingSeekSec: null,

    // --- library -----------------------------------------------------------

    upsertTracks: (incoming) =>
      set((s) => {
        // Both panels push the same cached catalogue; skip the write (and every
        // subscriber's re-render) when it brings nothing new.
        const changed = incoming.some((t) => s.tracks[t.id] !== t);
        // Stock tracks arrive as one ordered list; that list IS the library order.
        const stock = incoming.filter(isStockTrack).map((t) => t.id);
        const orderChanged =
          stock.length > 0 &&
          (stock.length !== s.libraryOrder.length || stock.some((id, i) => s.libraryOrder[i] !== id));
        if (!changed && !orderChanged) return {};
        const tracks = changed ? { ...s.tracks } : s.tracks;
        if (changed) for (const t of incoming) tracks[t.id] = t;
        return orderChanged ? { tracks, libraryOrder: stock } : { tracks };
      }),

    removeTrack: (id) =>
      set((s) => {
        const tracks = { ...s.tracks };
        delete tracks[id];
        const libraryOrder = s.libraryOrder.includes(id) ? s.libraryOrder.filter((x) => x !== id) : s.libraryOrder;
        const at = s.queue.indexOf(id);
        if (at === -1) return { tracks, libraryOrder };
        // Dropping it from the queue too, or the queue points at a track the
        // player can no longer describe.
        const next = removeAt(
          { ids: s.queue, currentIndex: s.currentIndex, shuffle: s.shuffle, shuffleOrder: s.shuffleOrder, repeat: s.repeat },
          at,
        );
        return { tracks, libraryOrder, queue: [...next.ids], currentIndex: next.currentIndex, shuffleOrder: [...next.shuffleOrder] };
      }),

    setLocalStatus: (id, status) =>
      set((s) => {
        const existing = s.tracks[id];
        if (!existing || !isLocalTrack(existing) || existing.status === status) return {};
        return { tracks: { ...s.tracks, [id]: { ...existing, status } } };
      }),

    // --- queue -------------------------------------------------------------

    playNow: (ids, startAtId) =>
      set((s) => {
        const queue = [...ids];
        const requested = startAtId ? queue.indexOf(startAtId) : 0;
        const startAt = requested >= 0 ? requested : 0;
        const shuffleOrder = s.shuffle ? buildShuffleOrder(queue.length, startAt) : [];
        return {
          queue,
          currentIndex: queue.length === 0 ? -1 : startAt,
          shuffleOrder,
          status: queue.length === 0 ? 'idle' : 'loading',
          positionSec: 0,
          durationSec: null,
          lastError: null,
        };
      }),

    enqueue: (ids) =>
      set((s) => {
        const existing = new Set(s.queue);
        const additions = ids.filter((id) => !existing.has(id));
        if (additions.length === 0) return {};
        const queue = [...s.queue, ...additions];
        return {
          queue,
          shuffleOrder: s.shuffle ? buildShuffleOrder(queue.length, s.currentIndex) : [],
          // Nothing was loaded, so start at the head of what just arrived.
          currentIndex: s.currentIndex < 0 ? s.queue.length : s.currentIndex,
        };
      }),

    removeFromQueue: (index) =>
      set((s) => {
        const next = removeAt(queueState(), index);
        return {
          queue: [...next.ids],
          currentIndex: next.currentIndex,
          shuffleOrder: [...next.shuffleOrder],
          status: next.ids.length === 0 ? 'idle' : s.status,
        };
      }),

    moveInQueue: (from, to) =>
      set(() => {
        const next = moveItem(queueState(), from, to);
        return { queue: [...next.ids], currentIndex: next.currentIndex };
      }),

    clearQueue: () =>
      set({
        queue: [],
        currentIndex: -1,
        status: 'idle',
        positionSec: 0,
        durationSec: null,
        shuffleOrder: [],
      }),

    // --- transport ---------------------------------------------------------

    play: () =>
      set((s) => {
        // Empty queue: play the stock library from the top, as any player does
        // when you press play with nothing chosen. Leaving the button inert
        // here read as a broken player.
        if (s.queue.length === 0) {
          const queue = s.libraryOrder.filter((id) => s.tracks[id]);
          if (queue.length === 0) return {};
          const startAt = s.shuffle ? Math.floor(Math.random() * queue.length) : 0;
          return {
            queue,
            currentIndex: startAt,
            shuffleOrder: s.shuffle ? buildShuffleOrder(queue.length, startAt) : [],
            status: 'loading',
            positionSec: 0,
            durationSec: null,
            lastError: null,
            userHasInteracted: true,
            scheduleAwaitingGesture: null,
          };
        }
        // Nothing selected yet: start at the head, honouring shuffle.
        if (s.currentIndex < 0) {
          const start = firstIndex({ ids: s.queue, shuffle: s.shuffle, shuffleOrder: s.shuffleOrder });
          if (start === null) return {};
          return { currentIndex: start, status: 'loading', userHasInteracted: true, scheduleAwaitingGesture: null };
        }
        return { status: 'playing', userHasInteracted: true, scheduleAwaitingGesture: null };
      }),

    pause: () => set((s) => (s.status === 'idle' ? {} : { status: 'paused', userHasInteracted: true })),

    togglePlay: () => {
      const s = get();
      if (s.status === 'playing') s.pause();
      else s.play();
    },

    next: (auto = false) =>
      set((s) => {
        const target = nextPlayableIndex(queueState(), playable, { auto });
        if (target === null) {
          // End of queue with repeat off, or nothing left that can play.
          return { status: 'paused', positionSec: 0 };
        }
        // Repeat-one lands on the same index; the host restarts it from zero.
        return {
          currentIndex: target,
          status: 'loading',
          positionSec: 0,
          durationSec: target === s.currentIndex ? s.durationSec : null,
          lastError: null,
        };
      }),

    previous: () =>
      set((s) => {
        // Restart the current track when a few seconds in — what every player
        // does, and what stops a mis-tap losing your place in the queue.
        if (s.positionSec > 3) return { pendingSeekSec: 0, positionSec: 0 };
        const target = previousIndex(queueState());
        if (target === null) return { pendingSeekSec: 0, positionSec: 0 };
        return { currentIndex: target, status: 'loading', positionSec: 0, durationSec: null, lastError: null };
      }),

    jumpTo: (index) =>
      set((s) => {
        if (index < 0 || index >= s.queue.length) return {};
        return {
          currentIndex: index,
          status: 'loading',
          positionSec: 0,
          durationSec: null,
          userHasInteracted: true,
          lastError: null,
          scheduleAwaitingGesture: null,
        };
      }),

    seek: (sec) =>
      set((s) => {
        const max = s.durationSec ?? Number.POSITIVE_INFINITY;
        const clamped = Math.max(0, Math.min(sec, max));
        return { pendingSeekSec: clamped, positionSec: Math.floor(clamped) };
      }),

    consumeSeek: () => {
      const pending = get().pendingSeekSec;
      if (pending !== null) set({ pendingSeekSec: null });
      return pending;
    },

    // --- settings ----------------------------------------------------------

    setVolume: (v) => {
      set({ volume: Math.max(0, Math.min(1, v)), muted: v === 0 ? get().muted : false });
      persist();
    },

    toggleMute: () => {
      set((s) => ({ muted: !s.muted }));
      persist();
    },

    toggleShuffle: () => {
      set((s) => {
        const shuffle = !s.shuffle;
        return {
          shuffle,
          // Regenerate on every switch: an order built for a different queue
          // length strands playback.
          shuffleOrder: shuffle ? buildShuffleOrder(s.queue.length, s.currentIndex) : [],
        };
      });
      persist();
    },

    cycleRepeat: () => {
      set((s) => ({
        repeat: s.repeat === 'off' ? 'all' : s.repeat === 'all' ? 'one' : 'off',
      }));
      persist();
    },

    setScheduleEnabled: (on) => {
      set({ scheduleEnabled: on, activeScheduleId: on ? get().activeScheduleId : null });
      persist();
    },

    // --- panel -------------------------------------------------------------

    setPanel: (panel) => set({ panel }),

    toggleCompact: () =>
      set((s) => ({ panel: s.panel === 'closed' ? 'compact' : 'closed' })),

    /** Escape steps expanded -> compact -> closed rather than jumping shut. */
    collapsePanel: () =>
      set((s) => ({ panel: s.panel === 'expanded' ? 'compact' : 'closed' })),

    // --- reports -----------------------------------------------------------

    reportStatus: (status) => set({ status }),

    reportPosition: (sec) =>
      set((s) => {
        const whole = Math.floor(sec);
        // The 4 Hz guard: only write when the displayed value actually changes.
        return whole === s.positionSec ? {} : { positionSec: whole };
      }),

    reportDuration: (sec) =>
      set((s) => {
        const value = sec !== null && Number.isFinite(sec) && sec > 0 ? Math.round(sec) : null;
        return value === s.durationSec ? {} : { durationSec: value };
      }),

    reportError: (message) => set({ lastError: message }),

    markInteracted: () => set((s) => (s.userHasInteracted ? {} : { userHasInteracted: true })),

    // --- schedule ----------------------------------------------------------

    setActiveSchedule: (id) => set((s) => (s.activeScheduleId === id ? {} : { activeScheduleId: id })),

    setScheduleAwaitingGesture: (id) =>
      set((s) => (s.scheduleAwaitingGesture === id ? {} : { scheduleAwaitingGesture: id })),
  };
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** The track currently loaded, or null. */
export function selectCurrentTrack(s: MusicState): Track | null {
  if (s.currentIndex < 0 || s.currentIndex >= s.queue.length) return null;
  return s.tracks[s.queue[s.currentIndex]] ?? null;
}

/** One queue position: the id, and the track if this device knows it. */
export interface QueueEntry {
  id: string;
  /** Null when nothing here can describe the id — see below. */
  track: Track | null;
}

/**
 * The queue, one entry per position, PRESERVING INDEX.
 *
 * The length must equal `queue.length`. An earlier version filtered out ids the
 * `tracks` map did not know, which silently desynchronised the UI from the
 * store: the list rendered its own map index and passed it to
 * `removeFromQueue`/`jumpTo`, which index `queue`. With one unknown id early in
 * the queue, every row below it removed or played the WRONG track, and the
 * "now playing" highlight sat on the wrong line.
 *
 * An unknown id is not hypothetical. A playlist can carry a `localRef` for a
 * file that lives on a different machine — that is the whole point of local
 * tracks — and `tracks` only ever holds local entries from THIS browser's
 * IndexedDB. So a manager's playlist opened on another device produces exactly
 * this, by design.
 *
 * MEMOISED on the `queue` and `tracks` references. Each entry is a fresh object,
 * so `useShallow` alone cannot stabilise this: it compares elements with
 * Object.is, and two `{ id, track }` objects are never identical. Unmemoised,
 * every snapshot read looked like a change and the panel crashed with React
 * error #185 the moment the queue became non-empty — i.e. on the first play.
 */
let entriesCache: { queue: string[]; tracks: MusicState['tracks']; result: QueueEntry[] } | null = null;

export function selectQueueEntries(s: MusicState): QueueEntry[] {
  if (entriesCache && entriesCache.queue === s.queue && entriesCache.tracks === s.tracks) {
    return entriesCache.result;
  }
  const result = s.queue.map((id) => ({ id, track: s.tracks[id] ?? null }));
  entriesCache = { queue: s.queue, tracks: s.tracks, result };
  return result;
}

/**
 * Just the tracks this device can describe.
 *
 * For counting and for saving a playlist, where position does not matter. Do NOT
 * use it for anything index-addressed — use `selectQueueEntries`.
 */
export function selectQueueTracks(s: MusicState): Track[] {
  return s.queue.map((id) => s.tracks[id]).filter((t): t is Track => Boolean(t));
}

/**
 * Whether pressing play does anything: a queue, or a library to start from.
 * A boolean, so it is safe to subscribe to without useShallow.
 */
export function selectCanPlay(s: MusicState): boolean {
  return s.queue.length > 0 || s.libraryOrder.length > 0;
}

export function selectCanGoNext(s: MusicState): boolean {
  if (s.queue.length === 0) return false;
  if (s.repeat === 'all') return true;
  return s.currentIndex < s.queue.length - 1;
}

export function selectCanGoPrevious(s: MusicState): boolean {
  if (s.queue.length === 0) return false;
  if (s.repeat === 'all') return true;
  return s.currentIndex > 0 || s.positionSec > 3;
}
